import React, { useState, useMemo, memo } from "react";
import { Table } from "antd";
import { FaUser } from "react-icons/fa";
import { checkIfTextExceedsLines, getMoment } from "../../../../../../../../utils/helper";

// :: CSS override สำหรับ Ant Design Table header สีเทา ::
const TABLE_CSS = `
  .custom-styled-antd-table .ant-table-thead > tr > th {
    background: #888888 !important;
    color: #ffffff !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    border-right: 1px solid #999999 !important;
  }
  .custom-styled-antd-table .ant-table-thead > tr > th:last-child {
    border-right: none !important;
  }
  .custom-styled-antd-table .ant-table-container {
    border-radius: 6px !important;
    overflow: hidden !important;
    border: 1px solid #d9d9d9 !important;
  }
  .custom-styled-antd-table .ant-table {
    border-radius: 6px !important;
  }
`;

// :: Expandable Text Component (สูงสุด 3 บรรทัด + ปุ่ม อ่านเพิ่มเติม / ย่อขนาด) ::
const ExpandableText = ({
  text,
  maxLines = 3,
  charsPerLine = 38,
  style,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text || text === "-") return null;

  const showToggle = checkIfTextExceedsLines(text, maxLines, charsPerLine);

  return (
    <div style={{ marginTop: "2px", ...style }} className={className}>
      <div
        style={{
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
          lineHeight: "1.45",
          ...(!isExpanded
            ? {
                display: "-webkit-box",
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : {}),
        }}
      >
        {text}
      </div>

      {showToggle && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          style={{
            color: "var(--primary, #00a99d)",
            fontSize: "11px",
            fontWeight: "600",
            cursor: "pointer",
            marginTop: "3px",
            display: "inline-block",
            userSelect: "none",
          }}
        >
          {isExpanded ? "ย่อขนาด" : "อ่านเพิ่มเติม"}
        </span>
      )}
    </div>
  );
};

/**
 * Table Component for displaying Important Information list
 *
 * @param {{
 *   importantList: Array<object>,
 *   isLoading: boolean,
 *   page: number,
 *   limit: number,
 *   total: number,
 *   onChangePage: (p: number) => void,
 * }} props
 */
const ImportantInfoTable = ({
  importantList = [],
  isLoading = false,
  page = 1,
  limit = 10,
  total = 0,
  onChangePage,
}) => {
  // columns ต้อง useMemo เพราะ render ลำดับขึ้นกับ page และ limit
  const columns = useMemo(
    () => [
      {
        title: "ลำดับ",
        key: "no",
        align: "center",
        width: 60,
        render: (_, __, index) => (page - 1) * limit + index + 1,
      },
      {
        title: "สถานะ / ข้อมูลสำคัญ",
        key: "status_reason",
        align: "left",
        width: 260,
        render: (_, record) => (
          <div style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
            <div
              style={{ fontWeight: "600", fontSize: "12px", color: "#1f2937" }}
            >
              {record.contact_result || "-"}
            </div>
            {record.contact_reason && record.contact_reason !== "-" && (
              <div
                style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}
              >
                {record.contact_reason}
              </div>
            )}
          </div>
        ),
      },
      {
        title: "รายละเอียด",
        dataIndex: "description",
        key: "description",
        align: "left",
        width: 260,
        render: (val) =>
          val && val !== "-" ? (
            <ExpandableText
              text={val}
              maxLines={3}
              style={{ fontSize: "12px", color: "#374151" }}
            />
          ) : (
            <span style={{ color: "#8c8c8c" }}>-</span>
          ),
      },
      {
        title: "วันที่ / เวลา",
        key: "datetime_agent",
        align: "left",
        width: 150,
        render: (_, record) => {
          const dateTimeStr = getMoment(
            record.create_date,
            "DD/MM/YYYY - HH:mm",
          );
          return (
            <div>
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "12px",
                  color: "#1f2937",
                  whiteSpace: "nowrap",
                }}
              >
                {dateTimeStr}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#6b7280",
                  marginTop: "3px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  whiteSpace: "nowrap",
                }}
              >
                <FaUser
                  style={{ fontSize: "10px", color: "#8c8c8c", flexShrink: 0 }}
                />
                <span>{record.create_by || "-"}</span>
              </div>
            </div>
          );
        },
      },
    ],
    [page, limit],
  );

  return (
    <>
      <style>{TABLE_CSS}</style>

      <Table
        className="custom-styled-antd-table"
        rowKey="id"
        bordered
        columns={columns}
        dataSource={importantList}
        loading={isLoading}
        // size="small"
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: onChangePage,
        }}
      />
    </>
  );
};

export default memo(ImportantInfoTable);
